import React, { useState, useEffect } from "react";
import { Wrapper } from "./style";
import { MakeApiCall } from "../../../apis";
import { message, Table, Button, Dropdown, Menu, Upload, Pagination, ConfigProvider, Spin } from "antd";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import dayjs from 'dayjs';
import { Helmet } from "react-helmet";
import AddExpense from "./lib/AddExpense";
import { exportToExcel } from "../../../utils/excelExport";

const Expenses = (props) => {
  const { userData } = props;

  const [totalPage, setTotalPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [marketplaceList, setMarketplaceList] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  // New upload related states
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  // export state
  const [exporting, setExporting] = useState(false);

  const getExpensesList = async (data) => {
    setLoading(true);
    const response = await MakeApiCall(
        `get-expenses?search_text=${data?.searchText || searchText}&page=${data?.page || page || 1}&per-page=${data?.pageSize || pageSize}`,
        "get",
        null,
        true,
        {},
        userData?.token
    );

    if (response?.status) {
      setTotalPage(response?.data?.pagination?.totalCount || 0);
      setPage(response?.data?.pagination?.page || 1);
      setList(response?.data?.records || []);
      setLoading(false);
    } else {
      message.warning(response?.message);
      setLoading(false);
      setList([]);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      // Request full data without pagination; include current search filter
      const response = await MakeApiCall(
          `get-expenses?search_text=${encodeURIComponent(searchText || "")}&no_pagination=true`,
          "get",
          null,
          true,
          {},
          userData?.token
      );

      if (response?.status) {
        const records = response?.data?.records || [];
        if (!records || records.length === 0) {
          message.warning("No records found to export");
        } else {
          // transform records into required export format (exact column headers)
          const formatted = records.map((r) => {
            // SKU handling: could be string, object or array
            let sku = "";
            if (r?.products) {
              if (Array.isArray(r.products)) {
                sku = r.products.map((p) => (p?.sku || p?.asin || p || "")).join(", ");
              } else if (typeof r.products === 'object') {
                sku = r.products.sku || r.products.asin || r.products.name || '';
              } else {
                sku = String(r.products);
              }
            }

            // Duration: if end_date present compute days difference, otherwise leave empty
            let duration = "";
            if (r?.end_date) {
              try {
                const days = dayjs(r.end_date).diff(dayjs(r.start_date), 'day');
                duration = `${days} days`;
              } catch (e) {
                duration = "";
              }
            }

            return {
              'Start Date': r?.start_date || "",
              'Frequency': r?.type || "",
              'Expense Title': r?.name || "",
              'Category': r?.category || "",
              'SKU': sku,
              'Marketplace': r?.marketplace_ids || "",
              'Amount': r?.amount || "",
              'Currency': r?.currency || "",
              'Duration': duration,
              '# of occurrences (optional)': r?.no_of_occurrences || "",
              'Amortize expense daily': r?.amortize_expense_daily ? 'Yes' : 'No',
              'Is Ad Cost?': r?.show_under_advertising_cost ? 'Yes' : 'No',
            };
          });

          // Export formatted rows with exact headers
          exportToExcel(formatted, {}, "expenses.xlsx");
          message.success("Export started");
        }
      } else {
        message.error(response?.message || "Failed to fetch expenses for export");
      }
    } catch (err) {
      console.error("Export error:", err);
      message.error("An error occurred while exporting expenses.");
    } finally {
      setExporting(false);
    }
  };

  const MarketplaceListAction = async () => {
    setLoading(true);
    setMarketplaceList([]);
    const response = await MakeApiCall(`get-marketplaces`, `get`, null, true, null, userData?.token);

    if (response?.status) {
      setLoading(false);
      setMarketplaceList(
          response.data.all_marketplaces?.map((d) => ({
            label: d?.sales_channel,
            value: d?.id,
          })) || []
      );
    } else {
      setMarketplaceList([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await getExpensesList({ page, pageSize });
    };
    fetchData();
  }, [page, pageSize, searchText]);

  useEffect(() => {
    MarketplaceListAction();
  }, []);

  const columns = [
    {
      title: "#",
      key: "index",
      render: (text, record, index) => {
        return (page - 1) * pageSize + index + 1;
      },
    },
    {
      title: "Date",
      dataIndex: "start_date",
      key: "start_date",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => (type !== null ? type : "N/A"),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name) => (name !== null ? name : "N/A"),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (category) => (category !== null ? category : "N/A"),
    },
    {
      title: "Advertising cost",
      dataIndex: "show_under_advertising_cost",
      key: "show_under_advertising_cost",
      render: (show_under_advertising_cost) => (show_under_advertising_cost !== null ? show_under_advertising_cost : "N/A"),
    },
    {
      title: "Product",
      dataIndex: "products",
      key: "products",
      render: (products) => (products !== null ? products : "N/A"),
    },
    {
      title: "Marketplace",
      dataIndex: "marketplace_ids",
      key: "marketplace_ids",
      render: (marketplace_ids) => (marketplace_ids !== null ? marketplace_ids : "N/A"),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (_, record) => (
          <>
            {record?.currency} {record?.amount !== null ? record?.amount : "N/A"}
          </>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, row) => (
          <Dropdown overlay={menu1(row)} trigger={["click"]}>
            <Button type="link" data-kt-menu-trigger="click" data-kt-menu-placement="bottom-end" data-kt-menu-overflow="true" >
              <i className="ki-solid ki-dots-vertical fs-1 text-gray-800 "></i>
            </Button>
          </Dropdown>
      ),
    },
  ];

  const menu1 = (row) => (
      <Menu className=" min-w-200px p-3">
        <Menu.Item key="1" className="d-flex align-items-center mb-2" onClick={() => handleEdit(row)}>
          <i className="ki-outline ki-pencil fs-3 me-2"></i>Edit Expenses
        </Menu.Item>
        <Menu.Item key="2" className="d-flex align-items-center" onClick={() => handleDelete(row?.id)}>
          <i className="ki-outline ki-trash fs-3 me-2"></i>Delete Expenses
        </Menu.Item>
      </Menu>
  );

  const theme = {
    token: {
      // your theme tokens...
      itemActiveBg: '#ffffff',
      colorPrimary: '#f45b68',
      // Add all other necessary tokens...
    },
  };

  const handleUpload = (file) => {
    const rawFile = file.originFileObj || file;
    setUploadedFile(rawFile);
    message.success(`${rawFile.name} file ready to upload.`);
    return false; // prevent automatic upload
  };

  const handleApplyClick = async () => {
    console.log('uploading ....');
    if (!uploadedFile) {
      message.error("Please upload a file before applying.");
      return;
    }
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);

      const response = await MakeApiCall(
          "import-expenses",
          "post",
          formData,
          true,
          {},
          userData?.token
      );

      if (response?.status) {
        message.success("File imported successfully!");
        setUploadedFile(null);
        getExpensesList();
      } else {
        message.error(response?.message || "Failed to import file");
      }

    } catch (error) {
      console.error("Error uploading file:", error);
      message.error("An error occurred during file upload.");
    } finally {
      setUploading(false);
    }
  };

  const menu = (
      <Menu>
        <Menu.Item key="1" disabled className="py-4">
          <div className="fs-5 text-gray-900 fw-bold">Import product data</div>
        </Menu.Item>
        <Menu.Divider />
        <div key="4" className="py-2 px-3">
          <div className="d-flex justify-content-between w-100">
            <p className="mb-0">Download a template</p>
            <a
                href="/assets/media/templates/Expense_Import_Template.xlsx"
                download="expenses_template.xlsx"
                className="text-danger"
            >
              Template <DownloadOutlined />
            </a>
          </div>
        </div>
        <Menu.Divider />
        <div key="5" className="py-2 px-3">
          <Upload
              beforeUpload={handleUpload}
              fileList={uploadedFile ? [uploadedFile] : []}
              onRemove={() => setUploadedFile(null)}
              maxCount={1}
          >
            <Button className="w-100 fs-7 h-45px border border-dashed border-danger">
              <UploadOutlined /> Drop files here or click to upload
            </Button>
          </Upload>
        </div>
        <Menu.Divider />
        <div key="6" className="py-2 px-3">
          <div className="d-flex justify-content-end">
            <button
                type="reset"
                className="btn btn-sm btn-light btn-active-light-danger me-2"
                onClick={() => setUploadedFile(null)}
            >
              Reset
            </button>
            <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={handleApplyClick}
                disabled={uploading || !uploadedFile}
            >
              {uploading ? 'Uploading...' : 'Apply'}
            </button>
          </div>
        </div>
      </Menu>
  );

  const handleSearch = (e) => {
    setSearchText(e.target.value);
    setPage(1);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const response = await MakeApiCall(
          editingExpense ? `update-expense/${editingExpense.id}` : "add-expense",
          "post",
          values,
          true,
          {},
          userData?.token
      );
      if (response?.status) {
        message.success("Expense saved successfully!");
        setIsModalVisible(false);
        getExpensesList();
      } else {
        message.error(response?.message || "Failed to save expense");
      }
    } catch (error) {
      console.error("Error saving expense:", error);
      message.error("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const response = await MakeApiCall(
          `delete-expense/${id}`,
          "delete",
          null,
          true,
          {},
          userData?.token
      );
      if (response?.status) {
        message.success("Expense deleted successfully!");
        getExpensesList();
      } else {
        message.error(response?.message || "Failed to delete expense");
      }
    } catch (error) {
      console.error("Error deleting expense:", error);
      message.error("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
      <Wrapper>
        <Helmet>
          <title>Expenses</title>
        </Helmet>

        <div className="mb-10 mx-n9 px-10 py-5 bg-white filter">
          <div className="position-relative ">
            <i className="ki-outline ki-magnifier text-gray-800 fs-2 position-absolute top-50 translate-middle-y ms-4" />
            <input
                type="text"
                value={searchText}
                onChange={handleSearch}
                className="form-control form-control-solid  text-dark  w-300px border border-gray-300 fs-7 ps-12 all-search"
                placeholder="Search by sku, title or asin..."
            />
          </div>
        </div>

        <div className="row gy-5 g-xl-5">
          <div className="col-xxl-12">
            <div className="card card-xxl-stretch mb-5 mb-xl-8">
              <div className="card-header border-bottom-dashed">
                <h3 className="card-title fw-bolder">Expenses List</h3>
                <div className="card-toolbar">
                  <button
                      className="btn btn-danger fs-7 fw-bold me-3"
                      onClick={() => {
                        setEditingExpense(null);
                        setIsModalVisible(true);
                      }}
                  >
                    Add Expenses <i className="ms-1 bi bi-plus fs-2"></i>
                  </button>
                  <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
                    <button className="btn btn-light-danger fs-7 fw-bold">
                      Import <i className="ms-1 bi bi-download fs-4"/>
                    </button>
                  </Dropdown>
                  <button
                      className="btn btn-dark fs-7 fw-bold ms-3 d-flex align-items-center"
                      onClick={handleExport}
                      disabled={exporting}
                  >
                    {exporting ? (
                        <>
                          <Spin size="small" className="me-2"/> Exporting...
                        </>
                    ) : (
                        <>
                          Export <i className="ms-1 bi bi-upload fs-4"/>
                        </>
                    )}
                  </button>
                 </div>
               </div>
               <div className="card-body py-2">
                 <div className="table-responsive">
                   <Table
                       scroll={{x: "max-content"}}
                       columns={columns}
                       loading={loading}
                       dataSource={list}
                       rowKey="key"
                       pagination={false}
                       components={{
                         table: (props) => (
                             <table className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7" {...props} />
                         ),
                         header: {
                           wrapper: (props) => <thead {...props} className="" />,
                           row: (props) => <tr {...props} className="fw-bolder text-dark bg-cus" />,
                           cell: (props) => <th {...props} />,
                         },
                       }}
                   />
                 </div>
               </div>
             </div>
           </div>
         </div>

        <div className="row gy-5 g-xl-5">
          <div className="col-xxl-12">
            <div className="card card-xxl-stretch mb-5 mb-xl-8">
              <div className="card-body py-2">
                <ConfigProvider theme={theme}>
                  <Pagination
                      className="mt-3"
                      current={page}
                      pageSize={pageSize}
                      total={totalPage}
                      showSizeChanger
                      onChange={(currentPage, newPageSize) => {
                        setPage(currentPage);
                        setPageSize(newPageSize);
                      }}
                      showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                  />
                </ConfigProvider>
              </div>
            </div>
          </div>
        </div>

        <AddExpense
            isModalVisible={isModalVisible}
            handleCancel={handleCancel}
            handleSubmit={handleSubmit}
            marketplaceList={marketplaceList}
            userData={userData}
            refreshExpenses={getExpensesList}
            editingExpense={editingExpense}
        />
      </Wrapper>
  );
};

export default Expenses;

